/**
 * ResetPasswordPage — the branded landing citizens hit after tapping the
 * password-reset link in their reset email.
 *
 * Reported defect (production, 2026-08-18):
 * A registered pre-VLK_ID citizen requested password reset, received the
 * email, tapped `https://veralok.com/reset-password?token=…`, and hit a
 * blank page. Two compounding root causes:
 *   1. The frontend SPA had no `/reset-password` route at all (App.js
 *      registered `/verify-email` but not this one), so the SPA rendered
 *      an empty React root.
 *   2. `EMAIL_APP_BASE_URL` defaulted to `veralok.com`, which — unlike
 *      `api.veralok.com` — has no SPA fallback in CloudFront and returns
 *      404 on `/reset-password?token=…`.
 *
 * This component is the (1) fix. Pairs with the Terraform runtime_env
 * change that points `EMAIL_APP_BASE_URL` at `api.veralok.com` (fix (2)).
 *
 * The flow has no dependency on VLK_ID, KYC status, wallet issuance or
 * any later identity-lifecycle state — a registered account that hasn't
 * finished enrollment can reset its password and continue enrollment
 * with the existing state intact.
 */
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowRight,
  Eye,
  EyeOff,
} from "lucide-react";
import VeraLokLogo from "./VeraLokLogo";
import "./design-system.css";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const STATES = {
  FORM: "form",
  SUBMITTING: "submitting",
  DONE: "done",
  MISSING_TOKEN: "missing_token",
  INVALID: "invalid",
  EXPIRED: "expired",
  ERROR: "error",
};

function useTokenFromQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search).get("token");
}

export default function ResetPasswordPage() {
  const token = useTokenFromQuery();
  const [state, setState] = useState(token ? STATES.FORM : STATES.MISSING_TOKEN);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 12) {
      setError("Use at least 12 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setState(STATES.SUBMITTING);
    try {
      const r = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      let body = null;
      try {
        body = await r.json();
      } catch (_) {
        /* body may be empty */
      }
      if (r.ok) {
        setState(STATES.DONE);
        return;
      }
      const detail = body?.detail || "";
      if (detail === "expired_token") setState(STATES.EXPIRED);
      else if (detail === "invalid_token") setState(STATES.INVALID);
      else {
        setError(typeof detail === "string" ? detail : "Reset failed.");
        setState(STATES.FORM);
      }
    } catch (_) {
      setError("Network error. Please try again.");
      setState(STATES.FORM);
    }
  }

  return (
    <div
      className="vlk-marketing min-h-screen flex flex-col"
      data-testid="reset-password-page"
    >
      <header className="px-6 py-5 border-b border-white/10">
        <Link to="/">
          <VeraLokLogo />
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {state === STATES.MISSING_TOKEN && (
            <Card
              icon={<AlertCircle className="text-amber-400" />}
              title="Reset link missing"
              body="This page needs a valid reset token. Please request a fresh reset email from the sign-in screen."
              cta={{ to: "/login", label: "Back to sign in" }}
              testid="reset-missing-token"
            />
          )}
          {state === STATES.INVALID && (
            <Card
              icon={<AlertCircle className="text-red-400" />}
              title="Link already used"
              body="This reset link has already been used. Request a fresh one from the sign-in screen."
              cta={{ to: "/login", label: "Back to sign in" }}
              testid="reset-invalid"
            />
          )}
          {state === STATES.EXPIRED && (
            <Card
              icon={<AlertCircle className="text-amber-400" />}
              title="Link expired"
              body="For security, reset links expire quickly. Request a fresh one from the sign-in screen."
              cta={{ to: "/login", label: "Back to sign in" }}
              testid="reset-expired"
            />
          )}
          {state === STATES.DONE && (
            <Card
              icon={<CheckCircle2 className="text-emerald-400" />}
              title="Password updated"
              body="Your password has been reset. Sign in on the Citizen App with your new password."
              cta={{ to: "/login", label: "Continue to sign in" }}
              testid="reset-done"
            />
          )}
          {(state === STATES.FORM || state === STATES.SUBMITTING) && (
            <form
              onSubmit={submit}
              className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-8"
              data-testid="reset-form"
            >
              <h1 className="text-2xl font-semibold text-white mb-2">
                Set a new password
              </h1>
              <p className="text-white/60 mb-6 text-sm">
                Choose something you haven't used before. 12 characters
                minimum.
              </p>

              <label className="text-xs uppercase tracking-wide text-white/50">
                New password
              </label>
              <div className="mt-1 relative">
                <input
                  type={reveal ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="w-full rounded-lg bg-black/40 border border-white/10 px-4 py-3 pr-11 text-white outline-none focus:border-white/40"
                  disabled={state === STATES.SUBMITTING}
                  required
                  minLength={12}
                />
                <button
                  type="button"
                  onClick={() => setReveal((r) => !r)}
                  aria-label={reveal ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50"
                >
                  {reveal ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <label className="text-xs uppercase tracking-wide text-white/50 mt-5 block">
                Confirm new password
              </label>
              <input
                type={reveal ? "text" : "password"}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-password-confirm"
                className="mt-1 w-full rounded-lg bg-black/40 border border-white/10 px-4 py-3 text-white outline-none focus:border-white/40"
                disabled={state === STATES.SUBMITTING}
                required
                minLength={12}
              />

              {error && (
                <p
                  className="mt-4 text-sm text-red-400"
                  data-testid="reset-error"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                data-testid="reset-submit"
                disabled={state === STATES.SUBMITTING}
                className="mt-6 w-full flex items-center justify-center gap-2 rounded-full bg-white text-black font-medium py-3 disabled:opacity-60"
              >
                {state === STATES.SUBMITTING ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <>
                    Update password <ArrowRight size={16} />
                  </>
                )}
              </button>

              <p className="mt-4 text-xs text-white/40">
                No enrollment progress or existing account data is
                affected — only your password changes.
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

function Card({ icon, title, body, cta, testid }) {
  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-8 text-center"
      data-testid={testid}
    >
      <div className="mx-auto mb-4 w-12 h-12 flex items-center justify-center rounded-full bg-white/5">
        {icon}
      </div>
      <h1 className="text-2xl font-semibold text-white mb-2">{title}</h1>
      <p className="text-white/60 mb-6 text-sm">{body}</p>
      <Link
        to={cta.to}
        className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-5 py-2.5"
      >
        {cta.label} <ArrowRight size={16} />
      </Link>
    </div>
  );
}
